import { UISref, useRouter } from "@uirouter/react";
import { FunctionComponent, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, PrimaryButton } from "../../../../../components/button";
import { Icon } from "../../../../../components/icons";
import { KycIcon } from "../../../../../components/icons/status";
import { Permission } from "../../../../../components/permission";
import { RenderFields, Table } from "../../../../../components/table";
import { Pageable } from "../../../../../services/pageable";
import { AppStore } from "../../../../../store";
import { countries } from "../../../../../store/common/models";
import { VerificationActions } from "../../../../../store/verification/actions";
import { VerificationDTO } from "../../../../../store/verification/models/dto/kyc";
import { VerificationService } from "../../../../../store/verification/service";
import { colours } from "../../../../../styles/colours";
import { FraudRoutes } from "../../../routes";
import { NoKyc } from "../empty";
import { FieldNames, useHeaders } from "./headers.hook";
import { MerchantName } from "./merchant-name";

export const PresentationalAllKyc: FunctionComponent<{
  kycList: VerificationDTO.Responses.GetAllKyc.T;
}> = ({ kycList }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const headers = useHeaders();

  const pageableState = useSelector<
    AppStore,
    AppStore["verification"]["allKyc"]["pagination"]
  >((store) => store.verification.allKyc.pagination);

  const newDataList = useMemo(
    () =>
      kycList.content.map(
        ({
          user: { firstName, lastName, address, emailAddress },
          userStatus,
          createdDate,
          fraudCheckId,
          companyId,
          ongoingBackgroundChecks,
        }) => ({
          firstName,
          lastName,
          country: address?.country,
          emailAddress,
          userStatus,
          companyId,
          createdDate,
          fraudCheckId,
          ongoingBackgroundChecks,
        })
      ),
    [kycList.content]
  );

  const onPaginationChange = useCallback(
    (
      x: Pageable.T<
        Exclude<
          FieldNames,
          "ongoingBackgroundChecks" | "fraudCheckId" | "companyId"
        >
      >
    ) => dispatch(VerificationActions.setAllKycPagination(x)),
    [dispatch]
  );

  const rowOnClick = useCallback(
    ({ fraudCheckId }) => {
      router.stateService.go(FraudRoutes.names.singleIndividualKyc, {
        fraudCheckId,
      });
    },
    [router]
  );

  const renderFields: RenderFields<typeof newDataList[number]> = useMemo(
    () => ({
      fraudCheckId: ({ fraudCheckId }) => (
        <UISref
          to={FraudRoutes.names.singleIndividualKyc}
          params={{ fraudCheckId }}
        >
          <a>
            <PrimaryButton variant="contained">
              <Icon type="export" rotateAmount={270} colour1={colours.white} />
            </PrimaryButton>
          </a>
        </UISref>
      ),
      userStatus: ({ userStatus }) => KycIcon(userStatus, "kyc"),
      createdDate: ({ createdDate }) => (
        <>{createdDate.local().format("yyyy-MM-DD HH:mm")}</>
      ),
      country: ({ country }) => (
        <>{country ? countries.enum[country as typeof countries.T] : null}</>
      ),
      companyId: ({ companyId }) => (
        <Permission roles={["ROLE_ADMIN"]}>
          <MerchantName id={companyId} />
        </Permission>
      ),
      ongoingBackgroundChecks: ({ fraudCheckId }) => (
        <Permission roles={["ROLE_ADMIN"]}>
          <Button
            colour="secondary"
            onClick={(e) => {
              e.stopPropagation();
              VerificationService.downloadKYCReport(fraudCheckId);
            }}
          >
            Report
          </Button>
        </Permission>
      ),
    }),
    [countries]
  );

  return kycList.pageable.totalElements < 1 ? (
    <NoKyc />
  ) : (
    <Table
      data={newDataList}
      head={headers}
      unsortable={["ongoingBackgroundChecks", "fraudCheckId", "companyId"]}
      renderFields={renderFields}
      rowOnClick={rowOnClick}
      pagination={kycList.pageable}
      onPaginationChange={onPaginationChange}
      pageableState={pageableState}
    />
  );
};
